import React, { useState, useEffect } from 'react';
import { techniciansApi, formatApiError } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { 
  Users, 
  Phone, 
  Mail, 
  Wrench,
  CheckCircle,
  Clock,
  XCircle,
  Loader2,
  Settings,
  Plus,
  Pencil,
  Trash2,
  AlertTriangle
} from 'lucide-react';

export default function TechniciansPage() {
  const { user } = useAuth();
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTech, setSelectedTech] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    specialty: 'general',
    phone: ''
  });
  const [editData, setEditData] = useState({
    name: '',
    specialty: 'general',
    phone: '',
    status: 'available'
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    fetchTechnicians();
  }, []);

  const fetchTechnicians = async () => {
    try {
      const response = await techniciansApi.getAll();
      setTechnicians(response.data);
    } catch (error) {
      console.error('Fetch technicians error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (techId, newStatus) => {
    try {
      await techniciansApi.updateStatus(techId, newStatus);
      fetchTechnicians();
    } catch (error) {
      console.error('Status change error:', error);
    }
  };

  const handleCreateTechnician = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');
    
    try {
      await techniciansApi.create(formData);
      setShowCreateModal(false);
      setFormData({ name: '', email: '', password: '', specialty: 'general', phone: '' });
      fetchTechnicians();
    } catch (error) {
      setFormError(formatApiError(error));
    } finally {
      setFormLoading(false);
    }
  };

  const handleEditTechnician = async (e) => {
    e.preventDefault();
    if (!selectedTech) return;
    
    setFormLoading(true);
    setFormError('');
    
    try {
      await techniciansApi.update(selectedTech.id, editData);
      setShowEditModal(false);
      setSelectedTech(null);
      fetchTechnicians();
    } catch (error) {
      setFormError(formatApiError(error));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteTechnician = async () => {
    if (!selectedTech) return;
    
    setFormLoading(true);
    setFormError('');
    
    try {
      await techniciansApi.delete(selectedTech.id);
      setShowDeleteModal(false);
      setSelectedTech(null);
      fetchTechnicians();
    } catch (error) {
      setFormError(formatApiError(error));
    } finally {
      setFormLoading(false);
    }
  };

  const openEditModal = (tech) => {
    setSelectedTech(tech);
    setEditData({
      name: tech.name,
      specialty: tech.specialty || 'general',
      phone: tech.phone || '',
      status: tech.status || 'available'
    });
    setFormError('');
    setShowEditModal(true);
  };

  const openDeleteModal = (tech) => {
    setSelectedTech(tech);
    setFormError('');
    setShowDeleteModal(true);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'available': return <CheckCircle className="w-5 h-5 text-[#16A34A]" />;
      case 'busy': return <Clock className="w-5 h-5 text-[#D97706]" />;
      case 'offline': return <XCircle className="w-5 h-5 text-[#71717A]" />;
      default: return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return 'bg-[#16A34A]';
      case 'busy': return 'bg-[#D97706]';
      case 'offline': return 'bg-[#71717A]';
      default: return 'bg-[#71717A]';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'available': return 'Disponibile';
      case 'busy': return 'Occupato';
      case 'offline': return 'Offline';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#002FA7]"></div>
      </div>
    );
  }

  // Stats
  const availableCount = technicians.filter(t => t.status === 'available').length;
  const busyCount = technicians.filter(t => t.status === 'busy').length;
  const offlineCount = technicians.filter(t => t.status === 'offline').length;
  const isAdmin = user?.role === 'admin';

  return (
    <div className="space-y-6" data-testid="technicians-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Gestione Tecnici
          </h1>
          <p className="text-[#71717A] mt-1">Monitora lo stato e le assegnazioni del team tecnico</p>
        </div>
        {isAdmin && (
          <Button 
            onClick={() => {
              setFormData({ name: '', email: '', password: '', specialty: 'general', phone: '' });
              setFormError('');
              setShowCreateModal(true);
            }}
            className="bg-[#16A34A] hover:bg-[#16A34A]/90 text-white font-medium shadow-sm"
            data-testid="add-technician-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Aggiungi Tecnico
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border border-[#E4E4E7] shadow-none" data-testid="stat-available">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#71717A]">Disponibili</p>
                <p className="text-2xl font-medium mt-1 text-[#16A34A]">{availableCount}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-[#16A34A] opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card className="border border-[#E4E4E7] shadow-none" data-testid="stat-busy">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#71717A]">Occupati</p>
                <p className="text-2xl font-medium mt-1 text-[#D97706]">{busyCount}</p>
              </div>
              <Clock className="w-8 h-8 text-[#D97706] opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card className="border border-[#E4E4E7] shadow-none" data-testid="stat-offline">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#71717A]">Offline</p>
                <p className="text-2xl font-medium mt-1 text-[#71717A]">{offlineCount}</p>
              </div>
              <XCircle className="w-8 h-8 text-[#71717A] opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Technicians Grid */}
      {technicians.length === 0 ? (
        <Card className="border border-[#E4E4E7] shadow-none">
          <CardContent className="p-12 text-center">
            <Users className="w-12 h-12 mx-auto mb-3 text-[#71717A] opacity-30" />
            <p className="text-[#71717A]">Nessun tecnico registrato</p>
            {isAdmin && (
              <Button 
                className="mt-4 bg-[#002FA7] text-white" 
                onClick={() => setShowCreateModal(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Aggiungi il primo tecnico
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {technicians.map((tech) => (
            <Card 
              key={tech.id} 
              className="border border-[#E4E4E7] shadow-none hover:-translate-y-1 hover:shadow-sm transition-all"
              data-testid={`tech-card-${tech.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="relative">
                      <div className="w-14 h-14 rounded-full bg-[#E4E4E7] flex items-center justify-center text-xl font-medium text-[#0F172A]">
                        {tech.name.charAt(0).toUpperCase()}
                      </div>
                      <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white ${getStatusColor(tech.status)}`}></div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-[#0F172A] truncate">{tech.name}</h3>
                      <p className="text-sm text-[#71717A] truncate">{tech.email}</p>
                    </div>
                  </div>
                  
                  {/* Admin Actions */}
                  {isAdmin && (
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-[#71717A] hover:text-[#002FA7]"
                        onClick={() => openEditModal(tech)}
                        data-testid={`edit-tech-${tech.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-[#71717A] hover:text-[#DC2626]"
                        onClick={() => openDeleteModal(tech)}
                        data-testid={`delete-tech-${tech.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-[#E4E4E7] space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-[#71717A]">
                      <Wrench className="w-4 h-4" />
                      <span className="capitalize">{tech.specialty}</span>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {tech.tickets_assigned} ticket
                    </Badge>
                  </div>

                  {tech.phone && (
                    <div className="flex items-center gap-2 text-sm text-[#71717A]">
                      <Phone className="w-4 h-4" />
                      <span>{tech.phone}</span>
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    {getStatusIcon(tech.status)}
                    <span className="text-sm font-medium">{getStatusLabel(tech.status)}</span>
                  </div>
                </div>

                {/* Status Change Buttons - Only for own profile or admin */}
                {(user?.id === tech.id || isAdmin) && (
                  <div className="mt-4 pt-4 border-t border-[#E4E4E7]">
                    <p className="text-xs text-[#71717A] mb-2">Cambia stato:</p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant={tech.status === 'available' ? 'default' : 'outline'}
                        className={tech.status === 'available' ? 'bg-[#16A34A] hover:bg-[#16A34A]/90' : ''}
                        onClick={() => handleStatusChange(tech.id, 'available')}
                        data-testid={`status-available-${tech.id}`}
                      >
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Disponibile
                      </Button>
                      <Button
                        size="sm"
                        variant={tech.status === 'busy' ? 'default' : 'outline'}
                        className={tech.status === 'busy' ? 'bg-[#D97706] hover:bg-[#D97706]/90' : ''}
                        onClick={() => handleStatusChange(tech.id, 'busy')}
                        data-testid={`status-busy-${tech.id}`}
                      >
                        <Clock className="w-3 h-3 mr-1" />
                        Occupato
                      </Button>
                      <Button
                        size="sm"
                        variant={tech.status === 'offline' ? 'default' : 'outline'}
                        className={tech.status === 'offline' ? 'bg-[#71717A] hover:bg-[#71717A]/90' : ''}
                        onClick={() => handleStatusChange(tech.id, 'offline')}
                        data-testid={`status-offline-${tech.id}`}
                      >
                        <XCircle className="w-3 h-3 mr-1" />
                        Offline
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Technician Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-[450px] bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Aggiungi Tecnico
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateTechnician} className="space-y-4 mt-2">
            {formError && (
              <div className="flex items-center gap-2 p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
                <AlertTriangle className="w-4 h-4" />
                {formError}
              </div>
            )}
            
            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-sm font-medium text-[#0F172A]">Nome Completo *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="Mario Rossi"
                data-testid="create-tech-name"
                className="h-10 border-[#E4E4E7] focus:border-[#002FA7] focus:ring-[#002FA7]"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-sm font-medium text-[#0F172A]">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                placeholder="tecnico@azienda.com"
                data-testid="create-tech-email"
                className="h-10 border-[#E4E4E7] focus:border-[#002FA7] focus:ring-[#002FA7]"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-sm font-medium text-[#0F172A]">Password *</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                placeholder="••••••••"
                data-testid="create-tech-password"
                className="h-10 border-[#E4E4E7] focus:border-[#002FA7] focus:ring-[#002FA7]"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="specialty" className="text-sm font-medium text-[#0F172A]">Specializzazione</Label>
                <Select
                  value={formData.specialty}
                  onValueChange={(value) => setFormData({ ...formData, specialty: value })}
                >
                  <SelectTrigger data-testid="create-tech-specialty" className="h-10 border-[#E4E4E7]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">Generale</SelectItem>
                    <SelectItem value="hardware">Hardware</SelectItem>
                    <SelectItem value="software">Software</SelectItem>
                    <SelectItem value="network">Rete</SelectItem>
                    <SelectItem value="security">Sicurezza</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="phone" className="text-sm font-medium text-[#0F172A]">Telefono</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+39 123 456 7890"
                  data-testid="create-tech-phone"
                  className="h-10 border-[#E4E4E7] focus:border-[#002FA7] focus:ring-[#002FA7]"
                />
              </div>
            </div>

            <DialogFooter className="mt-6 pt-4 border-t border-[#E4E4E7]">
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)} className="border-[#E4E4E7]">
                Annulla
              </Button>
              <Button 
                type="submit" 
                className="bg-[#002FA7] hover:bg-[#002FA7]/90 text-white"
                disabled={formLoading}
                data-testid="create-tech-submit"
              >
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Crea Tecnico'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Technician Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-[450px] bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Modifica Tecnico
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditTechnician} className="space-y-4 mt-2">
            {formError && (
              <div className="flex items-center gap-2 p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
                <AlertTriangle className="w-4 h-4" />
                {formError}
              </div>
            )}
            
            <div className="space-y-1.5">
              <Label htmlFor="edit-name" className="text-sm font-medium text-[#0F172A]">Nome Completo</Label>
              <Input
                id="edit-name"
                value={editData.name}
                onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                data-testid="edit-tech-name"
                className="h-10 border-[#E4E4E7] focus:border-[#002FA7] focus:ring-[#002FA7]"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="edit-specialty" className="text-sm font-medium text-[#0F172A]">Specializzazione</Label>
                <Select
                  value={editData.specialty}
                  onValueChange={(value) => setEditData({ ...editData, specialty: value })}
                >
                  <SelectTrigger data-testid="edit-tech-specialty" className="h-10 border-[#E4E4E7]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">Generale</SelectItem>
                    <SelectItem value="hardware">Hardware</SelectItem>
                    <SelectItem value="software">Software</SelectItem>
                    <SelectItem value="network">Rete</SelectItem>
                    <SelectItem value="security">Sicurezza</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="edit-status" className="text-sm font-medium text-[#0F172A]">Stato</Label>
                <Select
                  value={editData.status}
                  onValueChange={(value) => setEditData({ ...editData, status: value })}
                >
                  <SelectTrigger data-testid="edit-tech-status" className="h-10 border-[#E4E4E7]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="available">Disponibile</SelectItem>
                    <SelectItem value="busy">Occupato</SelectItem>
                    <SelectItem value="offline">Offline</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="edit-phone" className="text-sm font-medium text-[#0F172A]">Telefono</Label>
              <Input
                id="edit-phone"
                value={editData.phone}
                onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                placeholder="+39 123 456 7890"
                data-testid="edit-tech-phone"
                className="h-10 border-[#E4E4E7] focus:border-[#002FA7] focus:ring-[#002FA7]"
              />
            </div>

            <DialogFooter className="mt-6 pt-4 border-t border-[#E4E4E7]">
              <Button type="button" variant="outline" onClick={() => setShowEditModal(false)} className="border-[#E4E4E7]">
                Annulla
              </Button>
              <Button 
                type="submit" 
                className="bg-[#002FA7] hover:bg-[#002FA7]/90 text-white"
                disabled={formLoading}
                data-testid="edit-tech-submit"
              >
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salva Modifiche'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-[#DC2626]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Elimina Tecnico
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {formError && (
              <div className="flex items-center gap-2 p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
                <AlertTriangle className="w-4 h-4" />
                {formError}
              </div>
            )}
            
            <p className="text-[#71717A]">
              Sei sicuro di voler eliminare il tecnico <strong>{selectedTech?.name}</strong>?
            </p>
            <p className="text-sm text-[#71717A]">
              Questa azione non può essere annullata. Il tecnico non potrà più accedere alla piattaforma.
            </p>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDeleteModal(false)}>
                Annulla
              </Button>
              <Button 
                onClick={handleDeleteTechnician}
                className="bg-[#DC2626] hover:bg-[#DC2626]/90 text-white"
                disabled={formLoading}
                data-testid="delete-tech-confirm"
              >
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Elimina'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
